import { supabase } from '../../supabase';
import { toast } from 'sonner';
import { adminLog, adminError } from '@/lib/utils/admin-logger';
import { uploadPaperFile } from '../paper-storage';
import { incrementMajorVersion, incrementMinorVersion } from '@/lib/utils/version-helpers';
import { ensureBotUserExists } from '../bot-user';
import { initProcessingJobs, DEFAULT_UPLOAD_STEPS } from '@/lib/api/processing/initJobs';

export type VersionType = 'major' | 'minor';

export interface AddVersionParams {
  paperId: string;
  file: File;
  changeNotes: string[];
  versionType: VersionType;
  title?: string;
  abstract?: string;
}

export interface AddVersionResult {
  success: boolean;
  versionId?: string;
  newVersion?: string;
  /** Set when the version was published but the AI review was skipped because the user has 0 credits. */
  aiReviewSkippedForQuota?: boolean;
  error?: string;
}

/**
 * Add a new version to an existing paper
 */
export async function addPaperVersion(params: AddVersionParams): Promise<AddVersionResult> {
  const { paperId, file, changeNotes, versionType, title, abstract } = params;

  try {
    // Verify user is authenticated
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Authentication required');
      return { success: false, error: 'Not authenticated' };
    }

    // Fetch current paper data
    const { data: paper, error: paperError } = await supabase
      .from('papers')
      .select('id, title, abstract, current_version, author_id, authors, visibility, category')
      .eq('id', paperId)
      .single();

    if (paperError || !paper) {
      adminError('Failed to fetch paper:', paperError);
      toast.error('Paper not found');
      return { success: false, error: 'Paper not found' };
    }

    // Verify user is author
    const isMainAuthor = paper.author_id === user.id;
    const isCoAuthor = paper.authors && Array.isArray(paper.authors) && 
      paper.authors.some((author: { id?: string; isPlatformUser?: boolean }) => 
        author.id === user.id && author.isPlatformUser
      );

    if (!isMainAuthor && !isCoAuthor) {
      toast.error('Permission denied', {
        description: 'Only authors can add new versions'
      });
      return { success: false, error: 'Not authorized' };
    }

    // Calculate new version number
    const currentVersion = paper.current_version || '1.0';
    const newVersion = versionType === 'major' 
      ? incrementMajorVersion(currentVersion)
      : incrementMinorVersion(currentVersion);

    adminLog(`Creating version ${newVersion} for paper ${paperId}`);

    // Upload the new PDF file
    const fileUrl = await uploadPaperFile(file);
    if (!fileUrl) {
      return { success: false, error: 'File upload failed' };
    }

    adminLog('File uploaded successfully:', fileUrl);

    // Use provided title/abstract or fall back to paper's current values
    const versionTitle = title?.trim() || paper.title;
    const versionAbstract = abstract?.trim() || paper.abstract;
    const now = new Date().toISOString();

    // Insert new version record
    const { data: versionData, error: versionError } = await supabase
      .from('paper_versions')
      .insert({
        paper_id: paperId,
        version: newVersion,
        title: versionTitle,
        abstract: versionAbstract,
        file_url: fileUrl,
        published_at: now,
        changes: changeNotes.length > 0 ? changeNotes : ['Version update'],
      })
      .select()
      .single();

    if (versionError) {
      adminError('Failed to create version record:', versionError);
      toast.error('Failed to create version', {
        description: versionError.message
      });
      return { success: false, error: versionError.message };
    }

    // Update paper's current_version and optionally title/abstract
    const paperUpdate: Record<string, unknown> = {
      current_version: newVersion,
      updated_at: now
    };

    // Only update title/abstract if explicitly provided
    if (title?.trim()) {
      paperUpdate.title = title.trim();
    }
    if (abstract?.trim()) {
      paperUpdate.abstract = abstract.trim();
    }

    const { error: updateError } = await supabase
      .from('papers')
      .update(paperUpdate)
      .eq('id', paperId);

    if (updateError) {
      adminError('Failed to update paper version:', updateError);
      // Don't fail completely - version was created
      toast.warning('Version created but paper update failed');
    }

    // Trigger AI review for the new version.
    // The uploader always pays one credit per AI review run, regardless of
    // visibility — public user-uploaded papers are NOT exempt. ArXiv auto-
    // imports use a bot author so this code path doesn't apply to them.
    const isPrivate = paper.visibility === 'private';
    let aiReviewSkippedForQuota = false;

    const { data: canRequest, error: quotaError } = await supabase.rpc('can_request_ai_review', {
      user_id_param: user.id
    });
    if (quotaError) {
      adminError('Failed to check AI review quota:', quotaError);
    }
    if (!canRequest) {
      aiReviewSkippedForQuota = true;
    }

    if (!aiReviewSkippedForQuota) {
      try {
        await initProcessingJobs(paperId, DEFAULT_UPLOAD_STEPS);
        const botUserId = await ensureBotUserExists();
        if (botUserId) {
          await supabase.functions.invoke('generate-ai-review', {
            body: {
              paperId,
              pdfUrl: fileUrl,
              title: versionTitle,
              abstract: versionAbstract,
              reviewerId: botUserId,
              isPrivatePaperReview: isPrivate,
              paperVersion: newVersion,
              paperCategory: paper.category
            }
          });
          adminLog('AI review triggered for new version');

          // Bump per-paper counters for owner-initiated reviews (any visibility).
          // Credit consumption itself is handled atomically inside the
          // generate-ai-review edge function.
          const { data: paperCountRow } = await supabase
            .from('papers')
            .select('ai_review_count')
            .eq('id', paperId)
            .maybeSingle();
          const nextCount = ((paperCountRow as { ai_review_count?: number } | null)?.ai_review_count ?? 0) + 1;
          await supabase
            .from('papers')
            .update({
              ai_review_count: nextCount,
              last_ai_review_at: new Date().toISOString()
            })
            .eq('id', paperId);
        }
      } catch (aiError) {
        adminError('Failed to trigger AI review:', aiError);
        // Don't fail - AI review is optional
      }
    }

    if (aiReviewSkippedForQuota) {
      // Don't show a long toast here — the caller opens AIReviewQuotaDialog
      // with a clear "Buy credits & run review" CTA.
      toast.success('New version published', {
        description: `Version ${newVersion} is now live. AI review skipped — buy credits to run it.`
      });
    } else {
      toast.success('New version published', {
        description: `Version ${newVersion} is now live`
      });
    }

    return {
      success: true,
      versionId: versionData.id,
      newVersion,
      aiReviewSkippedForQuota,
    };

  } catch (error) {
    adminError('Unexpected error adding version:', error);
    toast.error('An unexpected error occurred');
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
}
